import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { uploadUrl, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Card, Field } from "../components/ui-ext";
import StatusPill from "../components/StatusPill";
import PhotoUpload from "../components/PhotoUpload";
import { ArrowLeft, Loader2, CheckCircle2, Lock, Unlock, Eye, EyeOff, Send } from "lucide-react";
import { toast } from "sonner";

const TIMELINE = ["Planned", "In Progress", "I&C", "NI", "AT", "SAT", "RFS", "Billed", "Paid"];

export default function TaskDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [task, setTask] = useState(null);
  const [masters, setMasters] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showUnlockRequest, setShowUnlockRequest] = useState(false);
  const [showHideDialog, setShowHideDialog] = useState(false);

  const load = () => api.get(`/tasks/${id}`).then((r) => setTask(r.data));

  useEffect(() => {
    load();
    api.get("/masters").then((r) => setMasters(r.data));
  }, [id]);

  if (!task) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#FF5722]" /></div>;

  const isEngineer = user.role === "engineer";
  const isOwner = task.engineer_id === user.id;
  const isLocked = !!task.is_locked;
  const canEdit = isEngineer ? (isOwner && !isLocked) : true;

  const save = async (patch) => {
    if (isEngineer && isLocked) {
      toast.error("Task is locked. Request unlock from your manager.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/tasks/${id}`, patch);
      setTask(data);
      toast.success("Task updated");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Update failed");
    } finally { setSaving(false); }
  };

  const doSubmit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/tasks/${id}/submit`);
      setTask(data);
      toast.success("Task submitted — locked from further edits");
      setShowSubmitConfirm(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Submit failed");
    } finally { setSaving(false); }
  };

  const doUnlockRequest = async (reason) => {
    setSaving(true);
    try {
      await api.post(`/tasks/${id}/unlock-request`, { reason });
      toast.success("Unlock request sent to manager");
      setShowUnlockRequest(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Request failed");
    } finally { setSaving(false); }
  };

  const doUnlock = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/tasks/${id}/unlock`, {});
      setTask(data);
      toast.success("Task unlocked");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  const currentStageIdx = (() => {
    if (task.status === "Done") return TIMELINE.indexOf("RFS");
    if (task.billing_status === "Paid") return TIMELINE.indexOf("Paid");
    if (task.billing_status === "Approved") return TIMELINE.indexOf("Billed");
    if (task.rfs_date) return TIMELINE.indexOf("RFS");
    if (task.sat_date) return TIMELINE.indexOf("SAT");
    if (task.at_date) return TIMELINE.indexOf("AT");
    if (task.ni_date) return TIMELINE.indexOf("NI");
    if (task.ic_date) return TIMELINE.indexOf("I&C");
    if (task.status === "Partial Done") return TIMELINE.indexOf("In Progress");
    return 0;
  })();

  return (
    <div>
      <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-4">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <PageHeader
        eyebrow={`${task.task_serial} · ${task.project_name}`}
        title={task.site_name || task.job_id || "Task"}
        subtitle={`${task.circle || ""} · ${task.state || ""} · ${task.city || ""}`}
        right={
          <div className="flex items-center gap-3 flex-wrap justify-end">
            <StatusPill status={task.status || "Planned"} />
            {isLocked && (
              <span data-testid="lock-badge" className="mono uppercase tracking-wider inline-flex items-center gap-1.5 border rounded-sm px-2 py-1 text-[10px]"
                style={{ color: "#F59E0B", background: "rgba(245,158,11,0.1)", borderColor: "rgba(245,158,11,0.35)" }}>
                <Lock className="w-3 h-3" /> Locked
              </span>
            )}
            {isEngineer && isOwner && !isLocked && (
              <button
                onClick={() => setShowSubmitConfirm(true)}
                data-testid="submit-task-btn"
                className="bg-[#FF5722] hover:bg-[#F4511E] text-white px-4 py-2 text-xs mono uppercase tracking-widest flex items-center gap-2 rounded-sm"
              >
                <Send className="w-3.5 h-3.5" /> Submit for Review
              </button>
            )}
            {isEngineer && isOwner && isLocked && (
              <button
                onClick={() => setShowUnlockRequest(true)}
                data-testid="request-unlock-btn"
                className="border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 px-4 py-2 text-xs mono uppercase tracking-widest flex items-center gap-2 rounded-sm"
              >
                <Unlock className="w-3.5 h-3.5" /> Request Unlock
              </button>
            )}
            {!isEngineer && isLocked && (
              <button
                onClick={doUnlock}
                data-testid="unlock-task-btn"
                className="border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] px-4 py-2 text-xs mono uppercase tracking-widest flex items-center gap-2 rounded-sm"
              >
                <Unlock className="w-3.5 h-3.5" /> Unlock
              </button>
            )}
            {!isEngineer && task.engineer_id && (
              <button
                onClick={() => setShowHideDialog(true)}
                data-testid="hide-task-btn"
                className="border border-white/15 hover:border-white/30 px-4 py-2 text-xs mono uppercase tracking-widest flex items-center gap-2 rounded-sm"
              >
                {(task.hidden_from_engineers || []).includes(task.engineer_id)
                  ? (<><Eye className="w-3.5 h-3.5" /> Unhide</>)
                  : (<><EyeOff className="w-3.5 h-3.5" /> Hide from Engineer</>)}
              </button>
            )}
          </div>
        }
      />

      {/* Pending unlock requests banner (visible to manager/admin) */}
      {!isEngineer && (task.unlock_requests || []).some((r) => r.status === "pending") && (
        <div className="border border-amber-500/40 bg-amber-500/5 p-4 mb-6 rounded-sm">
          <div className="flex items-start gap-3">
            <Unlock className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="mono text-[10px] uppercase tracking-widest text-amber-400 mb-1">Engineer requested unlock</div>
              {task.unlock_requests.filter((r) => r.status === "pending").map((r, i) => (
                <div key={i} className="text-sm text-slate-200 mt-1">
                  <span className="text-slate-500 mono text-xs">{new Date(r.requested_at).toLocaleString()} · </span>
                  {r.reason}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Locked banner for engineer */}
      {isEngineer && isLocked && (
        <div className="border border-amber-500/40 bg-amber-500/5 p-4 mb-6 rounded-sm">
          <div className="flex items-start gap-3">
            <Lock className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-amber-400 mb-1">Task Locked</div>
              <div className="text-sm text-slate-200">
                You submitted this task for review, so it's read-only now. Click "Request Unlock" if you need to modify anything.
              </div>
              {(task.unlock_requests || []).filter((r) => r.status === "pending").length > 0 && (
                <div className="mt-2 text-xs text-slate-400">
                  Unlock request pending manager approval.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left - Details */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#FF5722] mb-4">Site & Equipment</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Info label="Job ID" mono>{task.job_id || "—"}</Info>
              <Info label="Site ID" mono>{task.site_id || "—"}</Info>
              <Info label="Site Address" full>{task.site_address || "—"}</Info>
              <Info label="EQPT Brand">{task.eqpt_brand || "—"}</Info>
              <Info label="EQPT Type">{task.eqpt_type || "—"}</Info>
              <Info label="EQPT Qty" mono>{task.eqpt_qty ?? "—"}</Info>
              <Info label="NE Name" mono>{task.ne_name || "—"}</Info>
              <Info label="NE IP" mono>{task.ne_ip || "—"}</Info>
              <Info label="Serial No" mono>{task.serial_no || "—"}</Info>
              <Info label="Activity Planned">
                {Array.isArray(task.activity_planned)
                  ? (task.activity_planned.length ? task.activity_planned.join(", ") : "—")
                  : (task.activity_planned || "—")}
              </Info>
              <Info label="Billing Type">{task.billing_type || task.visit_type || "—"}</Info>
              <Info label="Visit Type">{task.visit_type || "—"}</Info>
              <Info label="Dependency">{task.dependency || "—"}</Info>
              <Info label="Direct Customer">{task.direct_customer || "—"}</Info>
              <Info label="End Customer">{task.end_customer || "—"}</Info>
              <Info label="SPOC">{task.customer_spoc || "—"}</Info>
              <Info label="Activity Rate" mono>₹ {task.activity_rate || 0}</Info>
              <Info label="Other Expense" mono>₹ {task.other_expense_rate || 0}</Info>
              <Info label="A. Total" mono>₹ {(task.activity_rate || 0) + (task.other_expense_rate || 0)}</Info>
              {task.remarks && <Info label="Remarks" full>{task.remarks}</Info>}
            </div>
          </Card>

          {/* Engineer completion form */}
          {((isEngineer && isOwner) || !isEngineer) && !(isEngineer && isLocked) ? (
            <Card className="p-6">
              <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#FF5722] mb-4">Update Equipment & Status</div>

              {/* NE / Serial - editable by engineer during/after activity */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <Field label="NE Name">
                  <input
                    className="input mono"
                    defaultValue={task.ne_name || ""}
                    onBlur={(e) => e.target.value !== (task.ne_name || "") && save({ ne_name: e.target.value })}
                    data-testid="task-ne-name"
                    placeholder="e.g. OLT-KOL-08X"
                  />
                </Field>
                <Field label="NE IP">
                  <input
                    className="input mono"
                    defaultValue={task.ne_ip || ""}
                    onBlur={(e) => e.target.value !== (task.ne_ip || "") && save({ ne_ip: e.target.value })}
                    data-testid="task-ne-ip"
                    placeholder="192.168.x.x"
                  />
                </Field>
                <Field label="Serial No">
                  <input
                    className="input mono"
                    defaultValue={task.serial_no || ""}
                    onBlur={(e) => e.target.value !== (task.serial_no || "") && save({ serial_no: e.target.value })}
                    data-testid="task-serial-no"
                    placeholder="Serial number"
                  />
                </Field>
              </div>

              {/* Activity Planned - multi-select */}
              <Field label="Activity Planned (select all applicable)">
                <div className="flex flex-wrap gap-2 border border-white/10 bg-[#0B0C10] p-2 rounded-sm">
                  {(masters?.activity_types || masters?.visit_types || []).map((v) => {
                    const current = Array.isArray(task.activity_planned)
                      ? task.activity_planned
                      : (task.activity_planned ? [task.activity_planned] : []);
                    const checked = current.includes(v);
                    return (
                      <button
                        type="button"
                        key={v}
                        onClick={() => {
                          const next = checked ? current.filter((x) => x !== v) : [...current, v];
                          save({ activity_planned: next });
                        }}
                        data-testid={`task-activity-${v}`}
                        className={`mono text-[10px] uppercase tracking-widest px-3 py-1.5 border rounded-sm transition-colors ${checked ? "bg-[#FF5722] text-black border-[#FF5722]" : "border-white/15 text-slate-400 hover:border-white/30"}`}
                      >
                        {v}
                      </button>
                    );
                  })}
                </div>
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <Field label="Status">
                  <select className="input" value={task.status || "Planned"} onChange={(e) => save({ status: e.target.value })} data-testid="task-status-select">
                    {masters?.statuses.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Activity Remarks">
                  <input className="input" defaultValue={task.activity_remarks || ""} onBlur={(e) => e.target.value !== (task.activity_remarks || "") && save({ activity_remarks: e.target.value })} data-testid="task-remarks" />
                </Field>
                <Field label="Gi Pipe Type">
                  <select className="input" value={task.gi_pipe_type || ""} onChange={(e) => save({ gi_pipe_type: e.target.value })}>
                    <option value="">—</option>
                    <option>Gi Pipe</option><option>Soft Conduit</option><option>Both</option>
                  </select>
                </Field>
                <Field label="Gi Pipe Length (m)">
                  <input type="number" className="input mono" defaultValue={task.gi_pipe_length || 0} onBlur={(e) => save({ gi_pipe_length: parseFloat(e.target.value) || 0 })} />
                </Field>
                <Field label="I&C Date"><input type="date" className="input" defaultValue={task.ic_date?.slice(0,10) || ""} onChange={(e) => save({ ic_date: e.target.value })} /></Field>
                <Field label="NI Date"><input type="date" className="input" defaultValue={task.ni_date?.slice(0,10) || ""} onChange={(e) => save({ ni_date: e.target.value })} /></Field>
                <Field label="AT Date"><input type="date" className="input" defaultValue={task.at_date?.slice(0,10) || ""} onChange={(e) => save({ at_date: e.target.value })} /></Field>
                <Field label="SAT Date"><input type="date" className="input" defaultValue={task.sat_date?.slice(0,10) || ""} onChange={(e) => save({ sat_date: e.target.value })} /></Field>
                <Field label="RFS Date"><input type="date" className="input" defaultValue={task.rfs_date?.slice(0,10) || ""} onChange={(e) => save({ rfs_date: e.target.value })} /></Field>
                {!isEngineer && (
                  <Field label="Billing Status">
                    <select className="input" value={task.billing_status || "N/A"} onChange={(e) => save({ billing_status: e.target.value })}>
                      {masters?.billing_statuses.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </Field>
                )}
              </div>

              <div className="mt-6">
                <PhotoUpload
                  value={task.completion_photos || []}
                  onChange={(v) => save({ completion_photos: v })}
                  label="Completion Photos"
                  testid="task-completion-photos"
                />
              </div>
              <div className="mt-4">
                <PhotoUpload
                  value={task.documents || []}
                  onChange={(v) => save({ documents: v })}
                  label="Documents / Reports"
                  testid="task-documents"
                />
              </div>
              <div className="mt-3 flex items-center gap-4">
                <Link to={`/expenses?task=${task.id}`} className="mono text-xs text-[#FF5722] hover:underline uppercase tracking-widest" data-testid="submit-expense-link">+ Submit Expense</Link>
                <Link to={`/live-requests?task=${task.id}`} className="mono text-xs text-[#FF5722] hover:underline uppercase tracking-widest" data-testid="submit-live-req-link">+ Live Request</Link>
              </div>
            </Card>
          ) : null}
        </div>

        {/* Right - Timeline */}
        <div>
          <Card className="p-6 sticky top-6">
            <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#FF5722] mb-5">Workflow Timeline</div>
            <div className="relative">
              {TIMELINE.map((stage, i) => {
                const done = i <= currentStageIdx;
                const active = i === currentStageIdx;
                return (
                  <div key={stage} className="flex gap-4 pb-6 last:pb-0 relative">
                    <div className="relative flex flex-col items-center">
                      <div className={`w-4 h-4 rounded-full border-2 z-10 ${done ? "bg-[#FF5722] border-[#FF5722]" : "bg-[#0B0C10] border-white/20"} ${active ? "pulse-dot" : ""}`}>
                        {done && <CheckCircle2 className="w-full h-full text-black" strokeWidth={2.5} />}
                      </div>
                      {i < TIMELINE.length - 1 && (
                        <div className={`absolute top-4 left-1/2 -translate-x-1/2 w-[2px] h-full ${done ? "bg-[#FF5722]/50" : "bg-white/10"}`} />
                      )}
                    </div>
                    <div className="pb-2">
                      <div className={`mono text-xs uppercase tracking-widest ${done ? "text-white" : "text-slate-500"}`}>{stage}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      </div>
      {saving && <div className="fixed bottom-4 right-4 bg-[#13151A] border border-white/10 px-4 py-2 flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Saving...</div>}

      {/* Submit confirmation */}
      {showSubmitConfirm && (
        <ConfirmDialog
          title="Submit task for review?"
          message="Once submitted, this task will be locked. To make further changes you'll have to request unlock from your manager. Make sure everything (photos, dates, remarks, activity types) is correct before submitting."
          confirmLabel="Yes, Submit & Lock"
          onClose={() => setShowSubmitConfirm(false)}
          onConfirm={doSubmit}
          busy={saving}
          testid="submit-confirm"
          danger={false}
        />
      )}

      {/* Unlock request */}
      {showUnlockRequest && (
        <UnlockRequestDialog onClose={() => setShowUnlockRequest(false)} onSubmit={doUnlockRequest} busy={saving} />
      )}

      {/* Hide/unhide dialog */}
      {showHideDialog && (
        <HideTaskDialog
          task={task}
          onClose={() => setShowHideDialog(false)}
          onDone={() => { setShowHideDialog(false); load(); }}
        />
      )}

      <style>{`.input { width: 100%; background: #0B0C10; border: 1px solid rgba(255,255,255,0.1); padding: 8px 12px; font-size: 13px; border-radius: 2px; color: white; }`}</style>
    </div>
  );
}

function ConfirmDialog({ title, message, confirmLabel, onClose, onConfirm, busy, testid, danger }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[#13151A] border border-white/10 w-full max-w-md">
        <div className="px-6 py-5">
          <h3 className="text-lg font-semibold mb-2">{title}</h3>
          <p className="text-sm text-slate-400">{message}</p>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button onClick={onClose} className="border border-white/15 px-4 py-2 text-sm rounded-sm">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid={`${testid}-confirm`}
            className={`${danger ? "bg-red-500 hover:bg-red-600" : "bg-[#FF5722] hover:bg-[#F4511E]"} text-white px-5 py-2 text-sm rounded-sm flex items-center gap-2`}
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function UnlockRequestDialog({ onClose, onSubmit, busy }) {
  const [reason, setReason] = useState("");
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[#13151A] border border-white/10 w-full max-w-md">
        <div className="px-6 py-4 border-b border-white/10">
          <div className="mono text-[10px] uppercase tracking-widest text-amber-400">Request Unlock</div>
          <h3 className="text-lg font-semibold">Explain why you need to modify this task</h3>
        </div>
        <div className="p-6">
          <textarea
            rows={4}
            className="input"
            placeholder="e.g. Need to update RFS date — customer changed schedule"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="unlock-reason"
            autoFocus
          />
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button onClick={onClose} className="border border-white/15 px-4 py-2 text-sm rounded-sm">Cancel</button>
          <button
            onClick={() => onSubmit(reason)}
            disabled={busy || !reason.trim()}
            data-testid="unlock-request-submit"
            className="bg-amber-500 hover:bg-amber-600 text-black px-5 py-2 text-sm rounded-sm flex items-center gap-2 disabled:opacity-40"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Send Request
          </button>
        </div>
        <style>{`.input { width: 100%; background: #0B0C10; border: 1px solid rgba(255,255,255,0.1); padding: 8px 12px; font-size: 13px; border-radius: 2px; color: white; }`}</style>
      </div>
    </div>
  );
}

function HideTaskDialog({ task, onClose, onDone }) {
  const isHidden = (task.hidden_from_engineers || []).includes(task.engineer_id);
  const [busy, setBusy] = useState(false);
  const doIt = async () => {
    setBusy(true);
    try {
      await api.post(`/tasks/${task.id}/${isHidden ? "unhide" : "hide"}`, { engineer_id: task.engineer_id });
      toast.success(isHidden ? "Task is visible again to engineer" : "Task hidden from engineer");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[#13151A] border border-white/10 w-full max-w-md">
        <div className="px-6 py-5">
          <h3 className="text-lg font-semibold mb-2">{isHidden ? "Unhide task from engineer?" : "Hide task from engineer?"}</h3>
          <p className="text-sm text-slate-400">
            {isHidden
              ? "This task will appear again in the assigned engineer's portal."
              : "The assigned engineer will no longer see this task in their portal. It remains fully visible to managers and admins."}
          </p>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button onClick={onClose} className="border border-white/15 px-4 py-2 text-sm rounded-sm">Cancel</button>
          <button
            onClick={doIt}
            disabled={busy}
            data-testid="hide-confirm"
            className="bg-[#FF5722] hover:bg-[#F4511E] text-white px-5 py-2 text-sm rounded-sm flex items-center gap-2"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {isHidden ? "Unhide" : "Hide"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Info({ label, children, full, mono }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">{label}</div>
      <div className={`text-sm text-white ${mono ? "mono" : ""}`}>{children}</div>
    </div>
  );
}
